import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import Sidebar from "../sidebar/Sidebar";
import SystemBar from "../systemBar/SystemBar";
import Navbar from "../navbar/Navbar";

const AppLayout = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const location = useLocation();
  const mainRef = useRef(null);
  // The element that opened the drawer (the hamburger), captured at click time —
  // before `.main` is inerted — so focus can return to it when the drawer closes.
  const openerRef = useRef(null);
  const wasDrawerActive = useRef(false);

  // The sidebar is a permanent rail at wider widths and an off-canvas drawer
  // only ≤1024px (see the `.sb` @media block in v3-tokens.css). Drawer-only
  // behaviors (focus trap, inert background, scroll-lock) must gate on this.
  const isDrawerMode = useMediaQuery("(max-width: 1024px)", { noSsr: true });
  const drawerActive = isDrawerMode && isNavOpen;

  // Auto-close the drawer on navigation.
  useEffect(() => {
    setIsNavOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer overlay is open (UX best practice; not a
  // WCAG SC). Escape-to-close is handled by useFocusTrap inside Sidebar.
  useEffect(() => {
    if (!drawerActive) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerActive]);

  // Make page content inert while the drawer overlay is open so keyboard and
  // screen-reader users cannot reach it behind the drawer (WCAG 2.4.3). React 17
  // does not reflect the JSX `inert` prop, so set it imperatively.
  // NOTE: this also inerts the hamburger toggle (it lives inside `.main`), and
  // `inert` can't be overridden on a descendant. Closing still works: the
  // backdrop (z-index 9) sits above the topbar/hamburger (z-index 5) and
  // intercepts the click, and Escape closes via the focus trap. Focus returns to
  // the hamburger via the restore effect below.
  useLayoutEffect(() => {
    const el = mainRef.current;
    if (el) el.inert = drawerActive;
  }, [drawerActive]);

  // Return focus to the opener (hamburger) when the drawer closes. PASSIVE on
  // purpose: it must run after the layout-phase `inert = false` above (so the
  // target is focusable again) and after Sidebar's focus-trap restore (passive
  // setups run child→parent, so this parent setup wins). Guarded to the close
  // transition so desktop and initial mount are untouched.
  useEffect(() => {
    if (wasDrawerActive.current && !drawerActive) {
      openerRef.current?.focus?.();
    }
    wasDrawerActive.current = drawerActive;
  }, [drawerActive]);

  // Toggle the drawer; on open, remember the opener (capture the DOM node, not
  // the synthetic event) so focus can return to it on close.
  const handleMenuClick = (e) => {
    if (!isNavOpen) openerRef.current = e.currentTarget;
    setIsNavOpen((v) => !v);
  };

  return (
    <>
      <Sidebar isOpen={isNavOpen} onClose={() => setIsNavOpen(false)} isDrawerMode={isDrawerMode} />
      <div
        className={"nav-backdrop" + (isNavOpen ? " nav-backdrop--show" : "")}
        onClick={() => setIsNavOpen(false)}
        aria-hidden="true"
      />
      <div className="main" ref={mainRef}>
        <SystemBar />
        <Navbar onMenuClick={handleMenuClick} isNavOpen={isNavOpen} />
        <Outlet />
      </div>
    </>
  );
};

export default AppLayout;
